import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, PieChart, MapPin, StopCircle, Share2, Copy, Users } from "lucide-react";
import { motion } from "framer-motion";
import { generateTripReport } from "@/utils/pdfGenerator";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { TripInsights } from "./TripInsights";
import { Expense } from "@/types/expense";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";

interface TripManagerProps {
  expenses: Expense[];
  tripName: string;
  onEndTrip?: () => void;
  shareId?: string;
}

export const TripManager = ({ expenses, tripName, onEndTrip, shareId }: TripManagerProps) => {
  const [showEndTripDialog, setShowEndTripDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const handleExportReport = () => {
    generateTripReport(tripName || "Current Trip", expenses);
    toast("Report Generated", {
      description: "PDF report has been downloaded."
    });
  };

  const handleEndTrip = () => {
    if (onEndTrip) {
      onEndTrip();
      setShowEndTripDialog(false);
      toast("Trip Ended", {
        description: "Your trip has been ended and moved to history."
      });
    }
  };

  const copyShareId = async () => {
    if (shareId) {
      await navigator.clipboard.writeText(shareId);
      toast.success("Share ID copied to clipboard!");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-4 md:p-6 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-2 w-full md:w-auto">
            <h2 className="text-xl md:text-2xl font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary animate-pulse" />
              {tripName || "Current Trip"}
            </h2>
            {shareId && (
              <div className="flex items-center gap-2 text-sm bg-white/50 p-2 rounded-lg border border-primary/20">
                <Share2 className="h-4 w-4 text-primary" />
                <code className="px-2 py-1 bg-white rounded border">{shareId}</code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={copyShareId}
                  className="h-8 flex items-center gap-1 hover:bg-primary/10"
                >
                  <Copy className="h-4 w-4" />
                  Copy ID
                </Button>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Track your expenses and manage your current trip
            </p>
          </div>
          <ScrollArea className="w-full md:w-auto">
            <div className="flex flex-wrap md:flex-nowrap gap-2 min-w-[300px] md:min-w-0">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2 whitespace-nowrap bg-white hover:bg-primary/5">
                    <PieChart className="h-4 w-4" />
                    Trip Insights
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
                  <DialogHeader>
                    <DialogTitle>Trip Insights</DialogTitle>
                    <DialogDescription>
                      Analyze your trip expenses and download reports
                    </DialogDescription>
                  </DialogHeader>
                  <div className="overflow-y-auto pr-4 max-h-[calc(80vh-8rem)]">
                    <TripInsights expenses={expenses} />
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 whitespace-nowrap bg-white hover:bg-primary/5"
                onClick={handleExportReport}
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button>

              {shareId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 whitespace-nowrap bg-white hover:bg-primary/5"
                  onClick={copyShareId}
                >
                  <Share2 className="h-4 w-4" />
                  Share Trip
                </Button>
              )}

              <Dialog open={showEndTripDialog} onOpenChange={setShowEndTripDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex items-center gap-2 whitespace-nowrap"
                  >
                    <StopCircle className="h-4 w-4" />
                    End Trip
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>End Trip</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to end this trip? This action will move all expenses to history and reset current trip data.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setShowEndTripDialog(false)}>Cancel</Button>
                    <Button variant="destructive" onClick={handleEndTrip}>End Trip</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </ScrollArea>
        </div>
      </Card>
    </motion.div>
  );
};